// Constant Contact Integration for Erika's Quiz Funnel
// Uses PKCE OAuth2 flow for secure authentication

class ConstantContactAPI {
  constructor() {
    this.clientId = process.env.REACT_APP_CONSTANT_CONTACT_CLIENT_ID;
    this.listId = process.env.REACT_APP_CONSTANT_CONTACT_LIST_ID;
    this.redirectUri = process.env.REACT_APP_CONSTANT_CONTACT_REDIRECT_URI;
    this.baseUrl = 'https://api.cc.email/v3';
    this.authUrl = 'https://authz.constantcontact.com/oauth2/default/v1/authorize';
    this.tokenUrl = 'https://authz.constantcontact.com/oauth2/default/v1/token';

    // Use proxy server for development, Firebase Function for production
    const isDevelopment = process.env.NODE_ENV === 'development';
    const functionUrl = process.env.REACT_APP_FIREBASE_FUNCTION_URL || 'https://us-central1-quiz-funnel-platform.cloudfunctions.net/api';

    this.proxyBaseUrl = isDevelopment
      ? 'http://localhost:3001/api/constant-contact'
      : `${functionUrl}/constant-contact`;
    this.proxyTokenUrl = isDevelopment
      ? 'http://localhost:3001/api/constant-contact/token'
      : `${functionUrl}/constant-contact/token`;
  }

  // Generate PKCE code verifier and challenge
  async generatePKCE() {
    const codeVerifier = this.generateRandomString(128);
    const hashBuffer = await this.sha256(codeVerifier);
    const codeChallenge = this.base64URLEncode(hashBuffer);
    return { codeVerifier, codeChallenge };
  }

  generateRandomString(length) {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return result;
  }

  async sha256(plain) {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    return await window.crypto.subtle.digest('SHA-256', data);
  }

  base64URLEncode(buffer) {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  // Start OAuth flow with PKCE
  async startOAuthFlow() {
    console.log('=== CONSTANT CONTACT OAUTH FLOW STARTED ===');
    console.log('Configuration check:', {
      clientId: this.clientId,
      redirectUri: this.redirectUri,
      authUrl: this.authUrl,
      hasClientId: !!this.clientId,
      hasRedirectUri: !!this.redirectUri
    });

    if (!this.clientId) {
      console.error('❌ CLIENT ID IS MISSING!');
      alert('Error: Constant Contact Client ID is not configured');
      return;
    }

    if (!this.redirectUri) {
      console.error('❌ REDIRECT URI IS MISSING!');
      alert('Error: Constant Contact Redirect URI is not configured');
      return;
    }

    try {
      // Generate PKCE parameters (required by Constant Contact)
      console.log('Generating PKCE parameters...');
      const { codeVerifier, codeChallenge } = await this.generatePKCE();

      const state = this.generateRandomString(32);

      const params = new URLSearchParams({
        response_type: 'code',
        client_id: this.clientId,
        redirect_uri: this.redirectUri,
        scope: 'contact_data',
        state: state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256'
      });

      const authorizationUrl = `${this.authUrl}?${params.toString()}`;

      console.log('=== OAUTH URL CONSTRUCTION WITH PKCE ===');
      console.log('Full Authorization URL:', authorizationUrl);
      console.log('Individual OAuth parameters:', Object.fromEntries(params));
      console.log('PKCE Code Challenge:', codeChallenge);

      // Store PKCE verifier and state for token exchange
      localStorage.setItem('cc_oauth_state', state);
      localStorage.setItem('cc_code_verifier', codeVerifier);

      console.log('About to redirect to:', authorizationUrl);

      // Redirect to Constant Contact authorization
      window.location.href = authorizationUrl;
    } catch (error) {
      console.error('Error starting OAuth flow:', error);
      alert('Error starting authentication: ' + error.message);
    }
  }

  // Exchange authorization code for access token
  async exchangeCodeForToken(code, state) {
    const storedState = localStorage.getItem('cc_oauth_state');
    const codeVerifier = localStorage.getItem('cc_code_verifier');

    if (state !== storedState) {
      throw new Error('Invalid state parameter');
    }

    if (!codeVerifier) {
      throw new Error('PKCE code verifier not found');
    }

    const tokenData = {
      grant_type: 'authorization_code',
      code: code,
      redirect_uri: this.redirectUri,
      client_id: this.clientId,
      code_verifier: codeVerifier
    };

    try {
      console.log('Exchanging code for token via proxy...');
      const response = await fetch(this.proxyTokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(tokenData)
      });

      if (!response.ok) {
        throw new Error(`Token exchange failed: ${response.statusText}`);
      }

      const tokens = await response.json();
      
      // Store tokens securely
      localStorage.setItem('cc_access_token', tokens.access_token);
      localStorage.setItem('cc_refresh_token', tokens.refresh_token);
      localStorage.setItem('cc_token_expires', Date.now() + (tokens.expires_in * 1000));

      // Clean up temporary storage
      localStorage.removeItem('cc_oauth_state');
      localStorage.removeItem('cc_code_verifier');

      return tokens;
    } catch (error) {
      console.error('Token exchange error:', error);
      throw error;
    }
  }

  // Get valid access token (refresh if needed)
  async getValidAccessToken() {
    const accessToken = localStorage.getItem('cc_access_token');
    const refreshToken = localStorage.getItem('cc_refresh_token');
    const expiresAt = localStorage.getItem('cc_token_expires');

    if (!accessToken) {
      throw new Error('No access token available. Please authenticate first.');
    }

    // Check if token is expired
    if (Date.now() >= parseInt(expiresAt)) {
      if (!refreshToken) {
        throw new Error('Token expired and no refresh token available');
      }
      return await this.refreshAccessToken(refreshToken);
    }

    return accessToken;
  }

  // Refresh access token
  async refreshAccessToken(refreshToken) {
    const tokenData = {
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: this.clientId
    };

    try {
      const response = await fetch(this.proxyTokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(tokenData)
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.statusText}`);
      }

      const tokens = await response.json();
      
      // Update stored tokens
      localStorage.setItem('cc_access_token', tokens.access_token);
      if (tokens.refresh_token) {
        localStorage.setItem('cc_refresh_token', tokens.refresh_token);
      }
      localStorage.setItem('cc_token_expires', Date.now() + (tokens.expires_in * 1000));

      return tokens.access_token;
    } catch (error) {
      console.error('Token refresh error:', error);
      // Clear invalid tokens
      this.clearTokens();
      throw error;
    }
  }

  // Add contact to Constant Contact list
  async addContact(email, firstName = '', lastName = '') {
    try {
      const accessToken = await this.getValidAccessToken();

      const contactData = {
        email_address: {
          address: email,
          permission_to_send: 'implicit'
        },
        first_name: firstName,
        last_name: lastName,
        create_source: 'Contact',
        list_memberships: [this.listId]
      };

      const response = await fetch(`${this.proxyBaseUrl}/v3/contacts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to add contact: ${errorData.message || response.statusText}`);
      }

      const result = await response.json();
      console.log('Contact added successfully:', result);
      return result;
    } catch (error) {
      console.error('Error adding contact to Constant Contact:', error);
      throw error;
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    const accessToken = localStorage.getItem('cc_access_token');
    const expiresAt = localStorage.getItem('cc_token_expires');
    
    return accessToken && Date.now() < parseInt(expiresAt);
  }

  // Clear stored tokens
  clearTokens() {
    localStorage.removeItem('cc_access_token');
    localStorage.removeItem('cc_refresh_token');
    localStorage.removeItem('cc_token_expires');
    localStorage.removeItem('cc_oauth_state');
    localStorage.removeItem('cc_code_verifier');
  }

  // Test the connection
  async testConnection() {
    try {
      const accessToken = await this.getValidAccessToken();
      
      const response = await fetch(`${this.proxyBaseUrl}/v3/account/summary`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Connection test failed: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Constant Contact connection successful:', data);
      return data;
    } catch (error) {
      console.error('Connection test failed:', error);
      throw error;
    }
  }
}

const constantContactAPI = new ConstantContactAPI();
export default constantContactAPI;
